import { Modal, Row } from "react-bootstrap";
import { SvgIcon } from "../../ui/SvgIcon";
import Close from "../../images/newDesign/close.nocolor.svg";
import { useTranslation } from "react-i18next";
import { SendReportModalStyled } from "./SendReportModal.styled";
import { useEffect, useState } from "react";
import Select from "react-select";
import { newSelectStyles } from "../shared/select.styled";
import { ErrorMessage, Field, Formik } from "formik";
import Input from "../simple/input";
import * as yup from "yup";
import StringUtils from "../utils/strings";
import { hideLoader, showLoader } from "../../store/common/slice";
import { fetchBuildingData, fetchManagerBuildingData } from "../../store/buildings/actions";
import * as Sentry from "@sentry/react";
import { useDispatch, useSelector } from "react-redux";
import { constants } from "../constants/constants";

/**
 * @typedef {{
 *     tenantId?: number,
 *     buildingId?: number,
 *     apartmentId?: number,
 *     currency?: string,
 *     email: string,
 *     phone: string,
 *     name: string,
 *     tenant_type: string,
 *     address: string,
 *     reportName: string,
 *     apartmentNumber: string,
 *     update_tenant_contact: boolean,
 *     set_tenant_as_building_manager: boolean,
 * }} SelectionData
 */

/**
 * @param {string} buildingID
 * @param {boolean} show
 * @param {() => void} onClose
 * @param {(SelectionData) => void} handleSelection
 * @return {JSX.Element}
 * @constructor
 */

export function SendReportModal({ buildingID, show, onClose = () => null, handleSelection }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const initialBuildingInfo = {
        address: '',
        apartmentNumbers: [],
        tenantDetails: '',
        apartments: [],
        currency: '',
    }
    const titleData = useSelector(state => state.buildings.titleData);

    const [initialValues, setInitialValues] = useState({});
    const [buildingInfo, setBuildingInfo] = useState(initialBuildingInfo);
    const [apartmentDropdownOptions, setApartmentDropdownOptions] = useState([]);

    const ownerDropdownOptions = [
        {
            value: constants.APIContract.tenantTypes.renter,
            label: t('payments.renter')
        },
        {
            value: constants.APIContract.tenantTypes.owner,
            label: t('payments.owner')
        }
    ];

    const formSchema = yup.object().shape({
        apartmentNumber: yup.object()
            .required(t('titles.forms.mandatory_field')).nullable(),
        tenant_type: yup.object()
            .required(t('titles.forms.mandatory_field')).nullable(),
        name: yup.string()
            .required(t('titles.forms.mandatory_field_full_name'))
            .min(2, t('titles.forms.min_length', { size: 2 }))
            .nullable(),
        phone: yup.string()
            .matches(/^[0-9]+$/, t('titles.forms.correct_phone'))
            .min(9, t('titles.forms.min_length', { size: 9 }))
            .max(10, t('titles.forms.max_length', { size: 10 }))
            .nullable(),
        email: yup.string()
            .required(t('titles.forms.correct_email')).nullable()
            .matches(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/, t('titles.forms.correct_email')),
        set_tenant_as_building_manager: yup.boolean(),
        update_tenant_contact: yup.boolean(),
    });

    /**
     * @param {string} buildingID
     * @return {Promise<void>}
     */
    const fetchData = async (buildingID) => {
        dispatch(showLoader());
        let tenantDetails = {};
        if (!buildingID) {
            setBuildingInfo(initialBuildingInfo);
            return;
        }
        const managerData = await dispatch(fetchManagerBuildingData({
            buildingID: buildingID,
            options: { urlParams: { skipDisabledCheck: true, skipStats: true } }
        }));
        const buildingData = await dispatch(fetchBuildingData({ buildingID: buildingID }));
        if (managerData) {
            tenantDetails = managerData.payload?.building?.tenants;
            if (tenantDetails == null || Object.keys(tenantDetails).length < 1) {
                Sentry.captureMessage('No tenant details available', 'error');
            }
        }
        if (buildingData) {
            const { payload } = buildingData;
            setBuildingInfo({
                address: t(`payments.${titleData.translationKey}`, titleData),
                apartmentNumbers: payload.allNumbers,
                tenantDetails: tenantDetails,
                apartments: payload.apartments,
                currency: buildingData.currency
            });
        }
        dispatch(hideLoader());
    };

    /**
     * @param {('renter' | 'owner' | '')} ownership
     * @param apartmentNumber
     * @param setFieldValue
     */
    const updateFields = (ownership, apartmentNumber, setFieldValue) => {
        const details = buildingInfo.tenantDetails?.[apartmentNumber]?.tenantListData;
        if (!details) return;
        let name = '';
        let phone = '';
        let email = '';
        if (ownership === 'renter') {
            name = details.RenterName;
            phone = details.RenterPhone;
            email = details.RenterEmail;
        }
        if (ownership === 'owner') {
            name = details.OwnerName;
            phone = details.OwnerPhone;
            email = details.OwnerEmail;
            setFieldValue('set_tenant_as_building_manager', false);
            setFieldValue('update_tenant_contact', false);
        }
        setFieldValue('name', name || '');
        setFieldValue('phone', phone || '');
        setFieldValue('email', email || '');
    };

    /**
     * @param e
     * @param setFieldValue
     */
    const handleApartmentChange = (e, setFieldValue) => {
        const selectedApartment = e.value;
        setFieldValue('apartmentNumber', e);
        const details = buildingInfo.tenantDetails?.[selectedApartment]?.tenantListData;
        let renterName = (details?.RenterName?.trim() || '');
        let ownerName = (details?.OwnerName?.trim() || '');
        const ownership = !!renterName ? constants.APIContract.tenantTypes.renter : (!!ownerName ? constants.APIContract.tenantTypes.owner : '');
        if (!ownership) {
            setFieldValue('tenant_type', '');
        } else {
            const selectedOwnership = ownerDropdownOptions.find(option => option.value === ownership);
            setFieldValue('tenant_type', selectedOwnership)
        }
        updateFields(ownership, selectedApartment, setFieldValue);
    };

    /**
     * @param e
     * @param values
     * @param setFieldValue
     */
    const handleOwnershipChange = (e, values, setFieldValue) => {
        setFieldValue('tenant_type', e);
        const ownership = e.value;
        const apartment = values.apartmentNumber;
        updateFields(ownership, apartment?.value, setFieldValue);
    };

    /**
     * @param values
     * @return {void}
     */
    const handleFormSubmit = (values) => {
        const emailWithoutSpecialChars = StringUtils.sanitizeString(values.email);
        const phoneWithoutSpecialChars = StringUtils.sanitizeString(values.phone);
        const details = buildingInfo.tenantDetails?.[values.apartmentNumber?.value]?.tenantListData;
        const tenantId = details[values.tenant_type?.value === 'renter' ? 'RenterId' : 'OwnerId'];
        const parsedValues = {
            ...values,
            email: emailWithoutSpecialChars,
            phone: phoneWithoutSpecialChars,
            apartmentNumber: values.apartmentNumber?.value,
            tenant_type: values.tenant_type?.value,
            address: buildingInfo?.address,
            tenantId,
            buildingId: buildingID,
            apartmentId: values.apartmentNumber?.id,
            currency: buildingInfo.currency,
        }
        onClose();
        handleSelection(parsedValues);
    };

    useEffect(async () => {
        if (show) {
            await fetchData(buildingID);
        }
    }, [show])

    useEffect(() => {
        let options = [];
        buildingInfo?.apartments?.map(ap => (
            options.push({
                value: ap.apartmentNumber,
                label: ap.apartmentNumber,
                id: ap.value,
            })
        ));
        setApartmentDropdownOptions(options)
    }, [buildingInfo.apartmentNumbers]);

    useEffect(() => {
        setInitialValues({
            apartmentNumber: '',
            tenant_type: '',
            name: '',
            phone: '',
            email: '',
            set_tenant_as_building_manager: false,
            update_tenant_contact: false,
        })
    }, [apartmentDropdownOptions])

    return (
        <SendReportModalStyled show={show} onHide={() => onClose()} centered>
            <Modal.Header>
                <button onClick={() => onClose()}>
                    <SvgIcon href={Close} size={16}/>
                </button>
            </Modal.Header>
            <Modal.Body>
                <div className="bllink-modal-title">{t('reports.sendReportModal.title')}</div>
                <center>
                    <Formik
                        enableReinitialize
                        initialValues={initialValues}
                        validationSchema={formSchema}
                        onSubmit={(values, helpers) => handleFormSubmit(values, helpers)}
                    >
                        {({
                              values,
                              handleChange,
                              handleBlur,
                              handleSubmit,
                              setFieldValue,
                              setFieldTouched
                          }) => {
                            return (
                                <form className="send-report-modal-form" name="sendReport" onSubmit={handleSubmit}>
                                    <Row className="mb-3">
                                        <div className='input-item col-md-6 col-12 send-report-modal-select'>
                                            <label className='label send-report-modal-label mx-3 mb-0'
                                                   htmlFor="apartmentNumber">*{t('payments.number_of_apartment')}</label>
                                            <Select
                                                name='apartmentNumber'
                                                styles={newSelectStyles}
                                                value={values.apartmentNumber}
                                                options={apartmentDropdownOptions}
                                                placeholder={t('payments.select_placeholder')}
                                                onBlur={() => setFieldTouched('apartmentNumber', true)}
                                                onChange={e => handleApartmentChange(e, setFieldValue)}
                                            />
                                            <ErrorMessage name="apartmentNumber">{msg => <div
                                                className="error">{msg}</div>}</ErrorMessage>
                                        </div>
                                        <div className='input-item col-md-6 col-12 send-report-modal-select'>
                                            <label className='label send-report-modal-label mx-3 mb-0'
                                                   htmlFor="ownership">*{t('payments.type_of_tenant')}</label>
                                            <Select
                                                name='tenant_type'
                                                value={values.tenant_type}
                                                styles={newSelectStyles}
                                                options={ownerDropdownOptions}
                                                placeholder={t('payments.select_placeholder')}
                                                onChange={e => handleOwnershipChange(e, values, setFieldValue)}
                                                onBlur={() => setFieldTouched('tenant_type', true)}
                                            />
                                            <ErrorMessage name="tenant_type">{msg => <div
                                                className="error">{msg}</div>}</ErrorMessage>
                                        </div>
                                    </Row>

                                    <Row>
                                        <div className="input-item col-md-6 col-12 send-report-modal-label">
                                            <Input
                                                name='name'
                                                label={`*${t('payments.full_name')}`}
                                                autocomplete='name'
                                                borderless
                                                value={values.name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            <ErrorMessage name="name">{msg => <div
                                                className="error">{msg}</div>}</ErrorMessage>
                                        </div>
                                        <div className="input-item col-md-6 col-12 send-report-modal-label">
                                            <Input
                                                name='phone'
                                                label={`${t('payments.phone')}`}
                                                autocomplete='tel'
                                                borderless
                                                value={values.phone}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            <ErrorMessage name="phone">{msg => <div
                                                className="error">{msg}</div>}</ErrorMessage>
                                        </div>
                                    </Row>
                                    <Row>
                                        <div className="input-item col-12 send-report-modal-label">
                                            <Input
                                                name='email'
                                                label={`*${t('payments.email')}`}
                                                autocomplete='email'
                                                borderless
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            <ErrorMessage name="email">{msg => <div
                                                className="error">{msg}</div>}</ErrorMessage>
                                        </div>
                                    </Row>
                                    <Row>
                                        <div className="form-check col-12 flex-column text-right mt-3 send-report-modal-checkbox">
                                            <label>
                                                <Field
                                                    type="checkbox"
                                                    id="set_tenant_as_building_manager"
                                                    name="set_tenant_as_building_manager"/>
                                                <span>{t('reports.sendReportModal.set_tenant_as_building_manager')}</span>
                                            </label>

                                            <label>
                                                <Field
                                                    type="checkbox"
                                                    id="update_tenant_contact"
                                                    name="update_tenant_contact"/>
                                                <span>{t('reports.sendReportModal.update_tenant_contact')}</span>
                                            </label>
                                        </div>
                                    </Row>
                                    <Row className="mt-3 row">
                                        <div
                                            className="form-button col-12 flex-container send-report-modal-form-button">
                                            <input
                                                id="select-tenant-to-send-report"
                                                type="submit"
                                                name="select-tenant_btn"
                                                value={t('reports.sendReportModal.send_to_email')}
                                            />
                                        </div>
                                    </Row>
                                </form>
                            )
                        }}
                    </Formik>
                </center>
            </Modal.Body>
        </SendReportModalStyled>
    );
}
